import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Save, 
  Loader2,
  Settings,
  Phone,
  Mail,
  MapPin,
  Globe,
  Link as LinkIcon,
  Plus,
  Trash2,
  Info,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const languages = [
  { code: 'ca', name: 'Català', flag: '🇦🇩' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
];

export const AdminSiteConfig = () => {
  const navigate = useNavigate();
  const { isAdmin, authFetch } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');
  const [activeLang, setActiveLang] = useState('ca');
  
  // Site config state
  const [siteConfig, setSiteConfig] = useState({
    contact_email: '',
    phone: '',
    whatsapp: '',
    address: '',
    address_line2: '',
    booking_url: '',
    members_url: '',
    maps_url: '',
    maps_embed_url: '',
    instagram_url: '',
    facebook_url: '',
    linkedin_url: ''
  });

  // Footer content state
  const [footerContent, setFooterContent] = useState({
    leclub_url: '',
    leclub_text: {},
    nav_title: {},
    legal_title: {},
    social_title: {},
    federations_title: {}
  });

  // Federations state
  const [federations, setFederations] = useState([]);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Només els administradors poden accedir a aquesta pàgina');
      navigate('/admin/dashboard');
      return;
    }
    fetchContent();
  }, [isAdmin, navigate]);

  const fetchContent = async () => {
    try {
      const [configRes, footerRes, fedRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/config`, {  }),
        fetch(`${API_URL}/api/footer-content`, {  }),
        fetch(`${API_URL}/api/admin/federations`, {  })
      ]);

      if (configRes.ok) {
        const data = await configRes.json();
        setSiteConfig(prev => ({ ...prev, ...data }));
      }

      if (footerRes.ok) {
        const data = await footerRes.json();
        setFooterContent(prev => ({ ...prev, ...data }));
      }

      if (fedRes.ok) {
        const data = await fedRes.json();
        setFederations(data.federations || []);
      }
    } catch (error) {
      console.error('Failed to fetch content:', error);
      toast.error('Error carregant configuració');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSiteConfig = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(siteConfig)
      });

      if (response.ok) {
        toast.success('Configuració del lloc guardada');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant configuració');
      }
    } catch (error) {
      toast.error('Error guardant configuració');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFooterContent = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/footer-content`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(footerContent)
      });

      if (response.ok) {
        toast.success('Contingut del footer guardat');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error guardant footer');
      }
    } catch (error) {
      toast.error('Error guardant footer');
    } finally {
      setSaving(false);
    }
  };

  const handleInitFooter = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/footer-content/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        toast.success('Footer inicialitzat');
        fetchContent();
      } else {
        const data = await response.json();
        toast.info(data.message || 'Footer ja existent');
      }
    } catch (error) {
      toast.error('Error inicialitzant footer');
    }
  };

  const handleInitFederations = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/federations/init`, {
        method: 'POST',
        
      });
      
      if (response.ok) {
        toast.success('Federacions inicialitzades');
        fetchContent();
      } else {
        const data = await response.json();
        toast.info(data.message || 'Federacions ja existents');
      }
    } catch (error) {
      toast.error('Error inicialitzant federacions');
    }
  };

  const updateFooterTextField = (field, value) => {
    setFooterContent(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [activeLang]: value
      }
    }));
  };

  const addFederation = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/federations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify({
          name: 'Nova Federació',
          abbr: 'NF',
          url: ''
        })
      });

      if (response.ok) {
        toast.success('Federació afegida');
        fetchContent();
      } else {
        toast.error('Error afegint federació');
      }
    } catch (error) {
      toast.error('Error afegint federació');
    }
  };

  const updateFederation = async (fedId, updates) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/federations/${fedId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        setFederations(prev => 
          prev.map(f => f.id === fedId ? { ...f, ...updates } : f)
        );
      } else {
        toast.error('Error actualitzant federació');
      }
    } catch (error) {
      toast.error('Error actualitzant federació');
    }
  };

  const deleteFederation = async (fedId) => {
    if (!window.confirm('Segur que vols eliminar aquesta federació?')) return;
    
    try {
      const response = await authFetch(`${API_URL}/api/admin/federations/${fedId}`, {
        method: 'DELETE',
        
      });

      if (response.ok) {
        setFederations(prev => prev.filter(f => f.id !== fedId));
        toast.success('Federació eliminada');
      } else {
        toast.error('Error eliminant federació');
      }
    } catch (error) {
      toast.error('Error eliminant federació');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-site-config">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-semibold text-[#1A4D2E] flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Configuració del Lloc
            </h1>
            <p className="text-[#666666] mt-1">
              Gestiona les dades de contacte, xarxes socials i footer
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#E5E5E5]">
          <button
            onClick={() => setActiveTab('contact')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'contact' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <Phone className="w-4 h-4 inline mr-2" />
            Contacte
          </button>
          <button
            onClick={() => setActiveTab('links')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'links' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <LinkIcon className="w-4 h-4 inline mr-2" />
            Enllaços
          </button>
          <button
            onClick={() => setActiveTab('footer')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'footer' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            <Globe className="w-4 h-4 inline mr-2" />
            Footer
          </button>
          <button
            onClick={() => setActiveTab('federations')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'federations' 
                ? 'border-[#1A4D2E] text-[#1A4D2E]' 
                : 'border-transparent text-[#666666] hover:text-[#333333]'
            }`}
          >
            Federacions ({federations.length})
          </button>
        </div>

        {activeTab === 'contact' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333] flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Dades de Contacte
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Telèfon principal</Label>
                  <Input
                    value={siteConfig.phone || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+34 661 19 40 23"
                    data-testid="config-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>WhatsApp</Label>
                  <Input
                    value={siteConfig.whatsapp || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, whatsapp: e.target.value }))}
                    placeholder="+34 676 047 987"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email de contacte</Label>
                <Input
                  value={siteConfig.contact_email || ''}
                  onChange={(e) => setSiteConfig(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="secretaria@aravellgolfclub.com"
                  data-testid="config-email"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Adreça (línia 1)</Label>
                  <Input
                    value={siteConfig.address || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Ctra. de Tor s/n, 25598 Aravell, Lleida"
                    data-testid="config-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Adreça (línia 2, opcional)</Label>
                  <Input
                    value={siteConfig.address_line2 || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, address_line2: e.target.value }))}
                    placeholder="25712 Aravell, Lleida"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveSiteConfig}
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
                data-testid="save-contact-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Contacte
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'links' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333] flex items-center gap-2">
                <LinkIcon className="w-4 h-4" />
                Enllaços Externs
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL de Reserva (TeeOne)</Label>
                  <Input
                    value={siteConfig.booking_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, booking_url: e.target.value }))}
                    placeholder="https://open.teeone.golf/es/aravell"
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL Àrea Membres</Label>
                  <Input
                    value={siteConfig.members_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, members_url: e.target.value }))}
                    placeholder="https://members.teeone.golf/aravell"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL Google Maps</Label>
                  <Input
                    value={siteConfig.maps_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, maps_url: e.target.value }))}
                    placeholder="https://maps.google.com/?q=..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>URL Embed Google Maps</Label>
                  <Input
                    value={siteConfig.maps_embed_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, maps_embed_url: e.target.value }))}
                    placeholder="https://maps.google.com/maps?q=..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Xarxes Socials</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={siteConfig.instagram_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, instagram_url: e.target.value }))}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Facebook</Label>
                  <Input
                    value={siteConfig.facebook_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, facebook_url: e.target.value }))}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>LinkedIn</Label>
                  <Input
                    value={siteConfig.linkedin_url || ''}
                    onChange={(e) => setSiteConfig(prev => ({ ...prev, linkedin_url: e.target.value }))}
                    placeholder="https://linkedin.com/..."
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleSaveSiteConfig}
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Enllaços
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="space-y-6">
            {/* Language selector */}
            <div className="flex gap-2 bg-[#F5F5F5] p-1 rounded-lg w-fit">
              {languages.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setActiveLang(l.code)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    l.code === activeLang 
                      ? 'bg-white text-[#1A4D2E] shadow-sm' 
                      : 'text-[#666666] hover:text-[#333333]'
                  }`}
                >
                  {l.flag} {l.code.toUpperCase()}
                </button>
              ))}
            </div>

            <div className="bg-white border border-[#E5E5E5] rounded-lg p-6 space-y-4">
              <h3 className="font-semibold text-[#333333]">Textos del Footer</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL LeClub Golf</Label>
                  <Input
                    value={footerContent.leclub_url || ''}
                    onChange={(e) => setFooterContent(prev => ({ ...prev, leclub_url: e.target.value }))}
                    placeholder="https://leclub-golf.com/es"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Text LeClub ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={footerContent.leclub_text?.[activeLang] || ''}
                    onChange={(e) => updateFooterTextField('leclub_text', e.target.value)}
                    placeholder="Membre de la xarxa LeClub Golf"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Títol Navegació ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={footerContent.nav_title?.[activeLang] || ''}
                    onChange={(e) => updateFooterTextField('nav_title', e.target.value)}
                    placeholder="Navegació"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol Legal ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={footerContent.legal_title?.[activeLang] || ''}
                    onChange={(e) => updateFooterTextField('legal_title', e.target.value)}
                    placeholder="Legal"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Títol Xarxes Socials ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={footerContent.social_title?.[activeLang] || ''}
                    onChange={(e) => updateFooterTextField('social_title', e.target.value)}
                    placeholder="Segueix-nos"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Títol Federacions ({activeLang.toUpperCase()})</Label>
                  <Input
                    value={footerContent.federations_title?.[activeLang] || ''}
                    onChange={(e) => updateFooterTextField('federations_title', e.target.value)}
                    placeholder="Federacions"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handleInitFooter}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Inicialitzar per defecte
              </Button>
              
              <Button
                onClick={handleSaveFooterContent}
                disabled={saving}
                className="bg-[#1A4D2E] hover:bg-[#153d25] text-white"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar Footer
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'federations' && (
          <div className="space-y-4">
            {federations.length === 0 ? (
              <div className="bg-[#F9F9F7] border border-dashed border-[#E5E5E5] rounded-lg p-8 text-center">
                <p className="text-[#666666] mb-4">No hi ha federacions configurades</p>
                <Button onClick={handleInitFederations} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Inicialitzar federacions per defecte
                </Button>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E5E5] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#F9F9F7]">
                    <tr>
                      <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3">Nom</th>
                      <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3">Abreviatura</th>
                      <th className="text-left text-xs font-semibold text-[#666666] uppercase px-4 py-3">URL</th>
                      <th className="text-center text-xs font-semibold text-[#666666] uppercase px-4 py-3">Accions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E5]">
                    {federations.map((fed) => (
                      <tr key={fed.id} data-testid={`fed-row-${fed.id}`}>
                        <td className="px-4 py-3">
                          <Input
                            value={fed.name || ''}
                            onChange={(e) => updateFederation(fed.id, { name: e.target.value })}
                            className="text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            value={fed.abbr || ''}
                            onChange={(e) => updateFederation(fed.id, { abbr: e.target.value })}
                            className="text-sm w-24"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Input
                            value={fed.url || ''}
                            onChange={(e) => updateFederation(fed.id, { url: e.target.value })}
                            className="text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteFederation(fed.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-between items-center pt-4">
              <Button
                variant="outline"
                onClick={addFederation}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Afegir Federació
              </Button>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSiteConfig;
